import { formatDate } from '@angular/common';
import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from 'ag-grid-enterprise';
import { DateTime } from 'luxon';
import { Observable } from 'rxjs';

import {
  buildColDef,
  ColType,
  columnSize,
} from 'app/shared/utils/ag-grid.col-def.utils';
import { formatCurrency, formatQuantity } from 'app/shared/utils/number.utils';
import { EApprovalStatus } from 'v2/modules/approval/interfaces/approval.interface';
import {
  EOrderCarrierCode,
  EOrderCarrierName,
} from 'v2/modules/order/interfaces/order-shipping.interface';
import { ISalesOrder } from 'v2/modules/order/interfaces/order.interface';
import {
  AgGridEditorDateComponent,
  IAgGridEditorDateParams,
} from 'v2/modules/shared/components/ag-grid-editor-date/ag-grid-editor-date.component';
import { AgGridConstants } from 'v2/modules/shared/constants/ag-grid.constants';
import { IIdName } from 'v2/modules/shared/interfaces/common.interface';
import { toCapitalize } from 'v2/modules/shared/util/string-transformation.utils';
import { UiExpandAllAgGridComponent } from 'v2/modules/ui/components/ui-expand-all-ag-grid/ui-expand-all-ag-grid.component';
import { UiSelectAllAgGridComponent } from 'v2/modules/ui/components/ui-select-all-ag-grid/ui-select-all-ag-grid.component';
import { EAddressVerifiedStatus } from 'v2/shared/contact/address-verified-status.const';

import { SalesOrderClickableCellRendererComponent } from '../components/sales-order-clickable-cell-renderer/sales-order-clickable-cell-renderer.component';
import { SalesOrderDropdownActionCellRendererComponent } from '../components/sales-order-dropdown-action-cell-renderer/sales-order-dropdown-action-cell-renderer.component';
import { SalesOrderInvoiceIdsCellRendererComponent } from '../components/sales-order-invoice-ids-cell-renderer/sales-order-invoice-ids-cell-renderer.component';
import { SalesOrderOrderIdCellRendererComponent } from '../components/sales-order-order-id-cell-renderer/sales-order-order-id-cell-renderer.component';
import { SalesOrderOrderStatusCellRendererComponentComponent } from '../components/sales-order-order-status-cell-renderer-component/sales-order-order-status-cell-renderer-component.component';
import { SalesOrderPicklistStatusCellRendererComponent } from '../components/sales-order-picklist-status-cell-renderer/sales-order-picklist-status-cell-renderer.component';
import { SalesOrderShipmentStatusCellRendererComponent } from '../components/sales-order-shipment-status-cell-renderer/sales-order-shipment-status-cell-renderer.component';
import { SalesOrderTagCellRendererComponent } from '../components/sales-order-tag-cell-renderer/sales-order-tag-cell-renderer.component';
import { SalesOrderTextColumnCellEditorComponent } from '../components/sales-order-text-column-cell-editor/sales-order-text-column-cell-editor.component';
import { ESalesOrderSourceValue } from '../interfaces/sales-order.interface';

import { SalesOrderAgTableConfig } from './sales-order-ag-table.config';
import { SalesOrderConfig } from './sales-order.config';

const SalesOrderDetailField = SalesOrderAgTableConfig.masterDetailRowFields;

/**
 * @example
 * foo.map(_toId);
 */
const _toIds = ({ id }) => id;

/**
 * @example
 * foo.filter(_whereIdIs(someValue));
 * foo.find(_whereIdIs(someValue));
 */
const _whereIdIs =
  (value) =>
  ({ id }) =>
    id === value;

export interface SalesOrderAgGridActionsCellParams {
  applyBackgroundColor(data :{ id :number; backgroundColor :string });
  openCreatePicklistModal(id :number);
  deleteSalesOrder(id :number);
  editSalesOrder(id :number);
  updateCustomFields(data :ISalesOrder);
  flagOrder(id :number);
  unFlagOrder(id :number);
  approveOrder(data :ISalesOrder);
  rejectOrder(data :ISalesOrder);
  setToPending(id :number);
  holdOrder(data :{
    id :number;
    orderNumber :string;
    source :string;
    shipstationOrderId ?:any;
  });
  updateTags(id :number);
  onSendEdiTransactionDocument(salesOrderId :number, ediTransactionNumber :string);
  openCreateTrackingNumber(data :{
    salesOrderId :number;
    trackingNumbers :string[];
  });
  printShippingLabel(data :any);
  createSalesOrderInvoice(shipstationOrderId :number);
  pushFulfillment(id :number);
  onUnpostOrder(id :number);
}
export interface SalesOrderAgGridColDefsBuildParams {
  actionsCellParams :SalesOrderAgGridActionsCellParams;

  getSourceValues() :string[];
  handleOrderNumberCellClick(params :any, field :string) :void;
  /** If set, the "Requested Ship Date" (`shipByDate`) cell will be editable. */
  saveShipByDateCell ?:(
    params :IAgGridEditorDateParams,
    value :string,
  ) => Observable<any>;
}

export function buildSalesOrderAgGridColDefs(
  buildParams :SalesOrderAgGridColDefsBuildParams,
) :ColDef[] {
  const SALES_ORDER_AG_GRID_COL_DEFS :ColDef[] = [
    buildColDef(ColType.CHECKBOX, {
      headerName: '',
      field: SalesOrderAgTableConfig.fields.CHECKBOX_SELECT_ALL,
      checkboxSelection: (params) => params.data?.isPosted,
      headerCheckboxSelection: false,
      headerCheckboxSelectionFilteredOnly: false,
      headerComponent: UiSelectAllAgGridComponent,
    }),
    buildColDef(ColType.EXPAND, {
      headerName: '',
      field: SalesOrderAgTableConfig.fields.MASTER_ROW,
      width: 24,
      maxWidth: 24,
      minWidth: 24,
      headerComponent: UiExpandAllAgGridComponent,
    }),
    {
      headerName: 'Order #',
      field: SalesOrderAgTableConfig.fields.ORDER_NUMBER,
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        defaultOption: 'contains',
        maxNumConditions: 1,
      },
      ...columnSize(16),
      cellRenderer: SalesOrderClickableCellRendererComponent,
      cellRendererParams: {
        propertyName: SalesOrderAgTableConfig.fields.ORDER_NUMBER,
        onClickEvent: (data :any) =>
          buildParams.handleOrderNumberCellClick(
            data,
            SalesOrderAgTableConfig.fields.ORDER_NUMBER,
          ),
      },
    },
    {
      headerName: 'Age',
      field: SalesOrderAgTableConfig.fields.AGE,
      sortable: true,
      filter: false,
      ...columnSize(12),
      valueFormatter: ({ data }) => {
        if (data?.orderDate) {
          const dateTime = DateTime.fromISO(data?.orderDate, { zone: 'utc' });

          return dateTime.toRelative();
        }

        return '';
      },
    },
    {
      headerName: 'Address Verified',
      field: SalesOrderAgTableConfig.fields.RECIPIENT_NAME,
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        defaultOption: 'contains',
        maxNumConditions: 1,
      },
      ...columnSize(20),
      valueGetter: ({ data }) => data?.shipTo?.name || '',
      cellRenderer: ({ data }) => {
        const addressVerified = data?.shipTo?.addressVerified;
        const isResidential = data?.shipTo?.residential;

        let recipient = data?.shipTo?.name || '';
        let cellClass = '';
        let cellIcon = '';

        switch (addressVerified) {
          case EAddressVerifiedStatus.SUCCESS:
            cellClass = 'cell-address-green mr-2';
            if (!isResidential) {
              cellIcon = 'fa fa-building';
            } else {
              cellIcon = 'fa fa-home';
            }
            break;
          case EAddressVerifiedStatus.WARNING:
            cellClass = 'cell-address-orange mr-2';
            cellIcon = 'fa fa-exclamation-triangle';
            break;
          case EAddressVerifiedStatus.FAILED:
            cellClass = 'cell-address-red mr-2';
            cellIcon = 'fa fa-exclamation-circle';
            break;
          case EAddressVerifiedStatus.NOT_FOUND:
          case EAddressVerifiedStatus.NOT_VERIFIED:
            cellClass = 'mr-2';
            cellIcon = 'fa fa-question-circle';
            break;

          default:
            if (
              data?.source === ESalesOrderSourceValue.LUMINOUS &&
              data?.shipstationOrderId === null
            ) {
              recipient = '';
            }
            break;
        }

        return `
          <i class="${cellIcon} ${cellClass} icon-address-medium"></i>
          ${toCapitalize(recipient, true)}
        `;
      },
    },
    buildColDef(
      ColType.DEFAULT,
      {
        headerName: 'Item SKU',
        field: SalesOrderAgTableConfig.fields.SKU,
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: [
            'contains',
            'notContains',
            'equals',
            'notEqual',
            AgGridConstants.FilterOption.HAS_ALL,
            AgGridConstants.FilterOption.NOT_HAS_ALL,
            AgGridConstants.FilterOption.HAS_ANY,
            AgGridConstants.FilterOption.NOT_HAS_ANY,
          ],
          filterPlaceholder: ({ filterOptionKey }) => {
            return [
              AgGridConstants.FilterOption.HAS_ALL,
              AgGridConstants.FilterOption.NOT_HAS_ALL,
              AgGridConstants.FilterOption.HAS_ANY,
              AgGridConstants.FilterOption.NOT_HAS_ANY,
            ]
              .map((option) => option.displayKey)
              .includes(filterOptionKey)
              ? 'SKU1,SKU2,SKU3...'
              : 'SKU...';
          },
          defaultOption: 'contains',
          maxNumConditions: 3,
        },
        ...columnSize(14),
        valueGetter: ({ data }) => {
          const items = data?.orderItems?.filter((item) => item.rfqId);

          return Array.isArray(items)
            ? items.map((item) => item.sku).join(', ')
            : '';
        },
        valueFormatter: ({ data }) => {
          const items = data?.orderItems?.filter((item) => item.rfqId);

          if (!Array.isArray(items) || items.length === 0) {
            return '';
          }

          return items.length === 1 ? items[0].sku : `(${items.length} items)`;
        },
      },
      {
        routerLinkFormatter: ({ data }) => {
          const items = data?.orderItems?.filter((item) => item.rfqId);

          // don't link empty fields or rows with multiple items
          if (!Array.isArray(items) || items.length !== 1) {
            return '';
          }
          const rfqId = items[0]?.rfqId;
          if (!rfqId) {
            return '';
          }

          return `/inventory/${items[0].rfqId}/view`;
        },
      },
    ),
    buildColDef(
      ColType.DEFAULT,
      {
        headerName: 'Item Name',
        field: SalesOrderAgTableConfig.fields.PRODUCT_NAME,
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          maxNumConditions: 1,
        },
        ...columnSize(24),
        valueGetter: ({ data }) => {
          const items = data?.orderItems?.filter((item) => item.rfqId);

          return Array.isArray(items)
            ? items.map((item) => item.name).join(', ')
            : '';
        },
        valueFormatter: ({ data }) => {
          const items = data?.orderItems?.filter((item) => item.rfqId);

          if (!Array.isArray(items) || items.length === 0) {
            return '';
          }

          return items.length === 1 ? items[0].name : `(${items.length} items)`;
        },
      },
      {
        routerLinkFormatter: ({ data }) => {
          const items = data?.orderItems?.filter((item) => item.rfqId);

          // don't link empty fields or rows with multiple items
          if (!Array.isArray(items) || items.length !== 1) {
            return '';
          }
          const rfqId = items[0]?.rfqId;
          if (!rfqId) {
            return '';
          }

          return `/inventory/${items[0].rfqId}/view`;
        },
      },
    ),
    {
      headerName: 'Picklist Status',
      field: SalesOrderAgTableConfig.fields.PICKING_STATUS,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: SalesOrderConfig.pickAndPackStatus.map(_toIds),
        valueFormatter: ({ value }) => {
          const pickAndPackStatus = SalesOrderConfig.pickAndPackStatus.find(
            _whereIdIs(value),
          );

          return pickAndPackStatus?.name;
        },
        newRowsAction: 'keep',
      },
      sortable: true,
      width: 180, // hard set to the max width it'll need for the longest status
      valueGetter: (params :any) => params.data?.pickingStatus,
      cellRenderer: SalesOrderPicklistStatusCellRendererComponent,
      cellRendererParams: (params :any) => ({
        pickingStatus: params?.data?.pickingStatus,
      }),
    },

    // DEFAULT HIDDEN COLUMNS
    buildColDef(
      ColType.DEFAULT,
      {
        hide: true,
        headerName: 'Buying Company',
        field: SalesOrderAgTableConfig.fields.BILL_TO_COMPANY,
        ...columnSize(21),
      },
      {
        routerLinkFormatter: ({ data }) =>
          data?.clientInvoice?.clientId
            ? `/clients/${data.clientInvoice.clientId}/profile`
            : '',
      },
    ),
    buildColDef(ColType.DEFAULT, {
      hide: true,
      headerName: 'Billing Contact',
      field: SalesOrderAgTableConfig.fields.BILL_TO_NAME,
      ...columnSize(21),
    }),
    {
      headerName: 'Order Date',
      field: SalesOrderAgTableConfig.fields.ORDER_DATE,
      sortable: true,
      hide: true,
      filter: 'agDateColumnFilter',
      ...columnSize(16),
      valueFormatter: ({ value }) =>
        value ? formatDate(value, 'mediumDate', 'en-US') : '',
    },
    {
      headerName: 'Notes',
      field: SalesOrderAgTableConfig.fields.INTERNAL_NOTES,
      sortable: false,
      filter: false,
      editable: true,
      hide: true,
      ...columnSize(20),
      cellEditorFramework: SalesOrderTextColumnCellEditorComponent,
    },
    {
      headerName: 'Quantity',
      field: SalesOrderAgTableConfig.fields.QUANTITY,
      sortable: false,
      filter: false,
      hide: true,
      ...columnSize(15),
      valueGetter: ({ data }) =>
        data?.orderItems?.reduce(
          (sum, item) => sum + (parseFloat(item.quantity) || 0),
          0,
        ) || 0,
      valueFormatter: ({ value }) => value?.toLocaleString(),
    },
    {
      headerName: 'Order Total',
      field: SalesOrderAgTableConfig.fields.ORDER_TOTAL,
      sortable: true,
      filter: false,
      hide: true,
      ...columnSize(15),
      valueFormatter: ({ value }) => formatCurrency(value),
    },
    {
      headerName: 'Ship Date',
      field: SalesOrderAgTableConfig.fields.SHIP_DATE,
      sortable: true,
      hide: true,
      filter: 'agDateColumnFilter',
      ...columnSize(16),
      valueFormatter: ({ value }) =>
        value ? formatDate(value, 'mediumDate', 'en-US') : '',
    },
    {
      headerName: 'Earliest Ship Date',
      field: SalesOrderAgTableConfig.fields.DO_NOT_SHIP_BEFORE_DATE,
      sortable: true,
      hide: true,
      filter: 'agDateColumnFilter',
      ...columnSize(16),
      valueFormatter: ({ value }) =>
        value ? formatDate(value, 'mediumDate', 'en-US') : '',
    },
    {
      headerName: 'Ship By Date',
      field: SalesOrderAgTableConfig.fields.REQUESTED_SHIP_DATE,
      sortable: true,
      editable: typeof buildParams.saveShipByDateCell === 'function',
      hide: true,
      filter: 'agDateColumnFilter',
      ...columnSize(16),
      cellEditor: AgGridEditorDateComponent,
      cellEditorParams:
        typeof buildParams.saveShipByDateCell === 'function'
          ? {
              save: (params :IAgGridEditorDateParams, value :string) =>
                buildParams.saveShipByDateCell(params, value),
            }
          : {},
      valueFormatter: ({ value }) =>
        value ? formatDate(value, 'mediumDate', 'en-US') : '',
    },
    {
      headerName: 'Approval Status',
      field: SalesOrderAgTableConfig.fields.APPROVAL_STATUS,
      hide: true,
      sortable: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: SalesOrderConfig.approvalStatus.map(_toIds),
        valueFormatter: ({ value }) => {
          const approvalStatus = SalesOrderConfig.approvalStatus.find(
            _whereIdIs(value),
          );

          return approvalStatus?.name;
        },
        newRowsAction: 'keep',
      },
      ...columnSize(12),
      valueGetter: (params :any) => params?.data?.approval?.status,
      valueFormatter: ({ data }) =>
        EApprovalStatus[data?.approval?.status ?? 'NONE'],
    },
    {
      headerName: 'Warehouse Group',
      field: SalesOrderAgTableConfig.fields.WAREHOUSE_GROUP,
      hide: true,
      sortable: true,
      filter: 'agSetColumnFilter',
      ...columnSize(16),
      valueFormatter: ({ data }) => data?.warehouseGroup?.label || '',
    },
    {
      headerName: 'Order ID',
      field: SalesOrderAgTableConfig.fields.ORDER_ID,
      hide: true,
      sortable: true,
      filter: 'agNumberColumnFilter',
      cellRenderer: SalesOrderOrderIdCellRendererComponent,
      ...columnSize(12),
    },
    buildColDef(
      ColType.INTEGER,
      {
        headerName: 'Invoice IDS',
        field: SalesOrderAgTableConfig.fields.INVOICE_IDS,
        hide: true,
        filter: 'agNumberColumnFilter',
        ...columnSize(16),
        cellRenderer: SalesOrderInvoiceIdsCellRendererComponent,
      },
      {},
    ),
    {
      headerName: 'Status',
      field: SalesOrderAgTableConfig.fields.IS_POSTED,
      hide: true,
      filterParams: {
        values: SalesOrderConfig.status.map(_toIds),
        valueFormatter: ({ value }) => SalesOrderConfig.status[value].name,
        newRowsAction: 'keep',
      },
      sortable: true,
      ...columnSize(13),
      filter: 'agSetColumnFilter',
      valueGetter: ({ data }) => (data?.isPosted ? 1 : 0),
      cellRenderer: ({ data }) => {
        if (data?.isPosted === undefined) {
          return '';
        }

        const isPosted = data?.isPosted;
        const status = isPosted ? 'Allocated' : 'Unallocated';
        const colorClass = isPosted ? 'green' : 'red';

        return `
          <mat-chip class="lum-chip lum-chip--small  mr-2">
            <mat-icon
              class="ag-status-circle cell-status-circle-${colorClass} mr-2"
            ></mat-icon>
            ${status}
          </mat-chip>
        `;
      },
    },
    {
      headerName: 'Order Status',
      field: SalesOrderAgTableConfig.fields.ORDER_STATUSES,
      hide: true,
      sortable: true,
      filter: false,
      filterParams: {
        values: SalesOrderConfig.orderStatus.map(_toIds),
        valueFormatter: ({ value }) => {
          const orderStatus = SalesOrderConfig.orderStatus.find(
            (row :IIdName) => row.id === value,
          );

          return orderStatus?.name;
        },
        newRowsAction: 'keep',
      },
      ...columnSize(13),
      valueGetter: ({ data }) => data?.orderStatus,

      cellRenderer: SalesOrderOrderStatusCellRendererComponentComponent,
      cellRendererParams: ({ data }) => ({ orderStatus: data?.orderStatus }),
    },
    {
      headerName: 'Description',
      field: SalesOrderAgTableConfig.fields.STARRED_DESCRIPTION,
      sortable: false,
      hide: true,
      filter: false,
      editable: true,
      cellEditorFramework: SalesOrderTextColumnCellEditorComponent,
      ...columnSize(20),
    },
    {
      headerName: 'Customer Notes',
      field: SalesOrderAgTableConfig.fields.CUSTOMER_NOTES,
      sortable: false,
      hide: true,
      filter: false,
      editable: true,
      cellEditorFramework: SalesOrderTextColumnCellEditorComponent,
      ...columnSize(20),
    },
    {
      headerName: 'Order Key',
      field: SalesOrderAgTableConfig.fields.ORDER_KEY,
      sortable: true,
      filter: 'agTextColumnFilter',
      hide: true,
      ...columnSize(16),
    },
    {
      headerName: 'Source',
      field: SalesOrderAgTableConfig.fields.SOURCE,
      sortable: true,
      hide: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: buildParams.getSourceValues(),
        valueFormatter: ({ value }) => {
          const source = SalesOrderConfig.sources.find(_whereIdIs(value));

          return source?.name;
        },
      },
      ...columnSize(10),
      valueGetter: ({ data }) => {
        const orderSource = data?.source.replace(/_/g, ' ').toLowerCase() || '';

        return toCapitalize(orderSource, true);
      },
    },
    {
      headerName: 'Channel',
      field: SalesOrderAgTableConfig.fields.CHANNEL_IDS,
      sortable: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: [], //Set empty array to prevent exception,
      },
      hide: true,
      valueGetter: (params :any) => {
        return params?.data?.channel?.id || '';
      },
      valueFormatter: (params :any) => params?.data?.channel?.label || '',
      ...columnSize(15),
    },
    {
      headerName: 'Tags',
      hide: true,
      field: SalesOrderAgTableConfig.fields.TAGS,
      sortable: false,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: [], //Set empty array to prevent exception,
      },
      autoHeight: true,
      wrapText: true,
      ...columnSize(16),
      cellClass: 'multiline',
      cellRenderer: SalesOrderTagCellRendererComponent,
      valueGetter: (params :any) => params?.data?.tags?.map((tag) => tag?.id) || [],
    },
    buildColDef(
      ColType.DEFAULT,
      {
        headerName: 'Shipping Name',
        field: SalesOrderAgTableConfig.fields.SHIPPING_NAME,
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          maxNumConditions: 1,
        },
        hide: true,
        valueGetter: ({ data }) => data?.shipTo?.name || '',
        ...columnSize(13),
      },
      {
        routerLinkFormatter: ({ data }) =>
          data?.clientInvoice?.clientId
            ? `/clients/${data.clientInvoice.clientId}/profile`
            : '',
      },
    ),
    buildColDef(
      ColType.DEFAULT,
      {
        headerName: 'Shipping Company',
        field: SalesOrderAgTableConfig.fields.SHIPPING_COMPANY,
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          maxNumConditions: 1,
        },
        hide: true,
        ...columnSize(16),
        valueGetter: ({ data }) =>
          data?.shipTo?.company ?? data?.clientInvoice?.client?.businessName,
        ...columnSize(14),
      },
      {
        routerLinkFormatter: ({ data }) =>
          data?.clientInvoice?.clientId
            ? `/clients/${data.clientInvoice.clientId}/profile`
            : '',
      },
    ),
    buildColDef(
      ColType.DEFAULT,
      {
        headerName: 'Shipping Address',
        field: SalesOrderAgTableConfig.fields.SHIPPING_ADDRESS,
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          maxNumConditions: 1,
        },
        wrapText: true,
        autoHeight: true,
        hide: true,
        ...columnSize(16),
        valueGetter: ({ data }) => {
          if (!data?.shipTo) {
            return '';
          }

          const { shipTo } = data;
          const addressComponents = [
            shipTo?.street1 ?? '',
            shipTo?.street2 ?? '',
            shipTo?.street3 ?? '',
            shipTo?.city ?? '',
            shipTo?.state || shipTo?.postalCode
              ? (shipTo?.state ?? '') + ' ' + (shipTo?.postalCode ?? '')
              : '',
            shipTo?.country ?? '',
          ];

          return `${addressComponents.filter(Boolean).join(', ')}`;
        },
      },
      {
        routerLinkFormatter: ({ data }) =>
          data?.clientInvoice?.clientId
            ? `/clients/${data.clientInvoice.clientId}/profile`
            : '',
      },
    ),
    {
      headerName: 'Shipment Tracking #s',
      field: SalesOrderAgTableConfig.fields.SHIPMENT_TRACKING_NUMBERS,
      sortable: true,
      filter: 'agTextColumnFilter',
      hide: true,
      wrapText: true,
      autoHeight: true,
      ...columnSize(16),
      valueGetter: ({ data }) => {
        if (!data?.trackingNumbers && !data?.shipstationShipments) {
          return '';
        }

        const rowToTrackingNumber = (row :{ trackingNumber :number | string }) =>
          row.trackingNumber;
        const manualTrackingNumbers =
          data?.trackingNumbers?.map?.(rowToTrackingNumber) ?? [];
        const shipmentTrackingNumbers =
          data?.shipstationShipments?.map?.(rowToTrackingNumber) ?? [];

        return [...shipmentTrackingNumbers, ...manualTrackingNumbers].join(', ');
      },
    },
    {
      headerName: 'Verified By',
      field: SalesOrderAgTableConfig.fields.ORDER_VERIFIED,
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        defaultOption: 'contains',
        maxNumConditions: 1,
      },
      hide: true,
      ...columnSize(11),
      valueGetter: ({ data }) => {
        if (!data?.picklistOrder?.verifiedBy) {
          return '';
        }

        const { firstName, lastName } = data.picklistOrder.verifiedBy;

        return `${firstName} ${lastName}`;
      },
    },
    {
      headerName: 'Verified Date',
      field: SalesOrderAgTableConfig.fields.VERIFIED_DATE,
      sortable: true,
      filter: 'agDateColumnFilter',
      hide: true,
      ...columnSize(12),
      valueGetter: ({ data }) => {
        if (!data?.picklistOrder) {
          return '';
        }

        return data.picklistOrder.verifiedAt;
      },
      valueFormatter: ({ data }) => {
        if (!data?.picklistOrder?.verifiedAt) {
          return '';
        }

        return (
          formatDate(data.picklistOrder.verifiedAt, 'mediumDate', 'en-US') ?? ''
        );
      },
    },
    {
      headerName: 'Invoice Payment Status',
      field: SalesOrderAgTableConfig.fields.INVOICE_PAYMENT_STATUS,
      sortable: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: SalesOrderConfig.invoicePaymentStatus.map((row :IIdName) => row.id),
        valueFormatter: ({ value }) => {
          const invoicePaymentStatus = SalesOrderConfig.invoicePaymentStatus.find(
            (row :IIdName) => row.id === value,
          );

          return invoicePaymentStatus?.name;
        },
      },
      hide: true,
      ...columnSize(16),
      valueGetter: (params :any) => {
        if (params?.data?.clientInvoice) {
          return params?.data?.clientInvoice?.paymentStatus;
        }

        return '';
      },
      cellRenderer: (params :any) => {
        const { data } = params;
        if (data?.clientInvoice) {
          const { clientInvoice } = data;
          switch (clientInvoice.paymentStatus) {
            case 0: // Unpaid
              return `
              <mat-chip
                class="lum-chip lum-chip--small"
              >
              <mat-icon class="ag-status-circle mr-2"></mat-icon>
              Unpaid
              </mat-chip>`;

            case 1: // Fully Paid
              return `
              <mat-chip
                class="lum-chip lum-chip--small"
              >
              <mat-icon class="ag-status-circle mr-2"></mat-icon>
              Fully Paid
              </mat-chip>`;

            case 2: // Partially Paid
              return `
              <mat-chip
                class="lum-chip lum-chip--small"
              >
              <mat-icon class="ag-status-circle mr-2"></mat-icon>
              Partially Paid
              </mat-chip>`;

            case 3: // Pending Payment
              return `
                <mat-chip
                  class="lum-chip lum-chip--small"
                >
                <mat-icon class="ag-status-circle mr-2"></mat-icon>
                Pending Payment
                </mat-chip>`;

            default:
              break;
          }
        }

        return '';
      },
    },

    {
      headerName: 'Order Type',
      field: SalesOrderAgTableConfig.fields.TYPE,
      sortable: true,
      filterParams: {
        values: SalesOrderConfig.orderTypes.map(_toIds),
        valueFormatter: ({ value }) => {
          const orderType = SalesOrderConfig.orderTypes.find(_whereIdIs(value));

          return orderType?.name;
        },
      },
      filter: true,
      hide: true,
      ...columnSize(14),
      valueGetter: (params :any) => params?.data?.type,
      cellRenderer: (params :any) => {
        const orderType = params?.data?.type.replace(/_/g, ' ').toLowerCase() || '';

        return `
          <mat-chip
          class="lum-chip lum-chip--small"
        >
        <mat-icon class="ag-status-circle mr-2"></mat-icon>
        ${toCapitalize(orderType, true)}
        </mat-chip>`;
      },
    },
    {
      headerName: 'Carrier',
      field: SalesOrderAgTableConfig.fields.CARRIER_CODE,
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        defaultOption: 'contains',
        maxNumConditions: 1,
      },
      hide: true,
      ...columnSize(15),
      valueGetter: ({ data }) => data?.carrierCode,
      cellRenderer: ({ data }) => {
        let src = null;
        let altDescription = null;
        const width = 24;
        const height = 18;

        switch (data?.carrierCode) {
          case EOrderCarrierCode.STAMPS_COM:
            src = '/assets/icons/svgs/stamps-com.svg';
            altDescription = EOrderCarrierName.STAMPS_COM;
            break;
          case EOrderCarrierCode.UPS_WALLETED:
            src = '/assets/icons/svgs/ups.svg';
            altDescription = EOrderCarrierName.UPS_WALLETED;
            break;
          case EOrderCarrierCode.GLOBALPOST:
            src = '/assets/icons/svgs/globalpost.svg';
            altDescription = EOrderCarrierName.GLOBALPOST;
            break;
          case EOrderCarrierCode.USPS:
            src = '/assets/icons/svgs/usps.svg';
            altDescription = EOrderCarrierName.USPS;
            break;
          default:
            break;
        }

        if (src) {
          return `
            <img src="${src}" alt="${altDescription}" class="icon" width="${width}" height="${height}" />
            <span>${data?.carrierCode}</span>
          `;
        }

        return `${data?.carrierCode ?? ''}`;
      },
    },
    {
      headerName: 'OMS Order Status',
      field: SalesOrderAgTableConfig.fields.OMS_STATUS,
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        defaultOption: 'contains',
        maxNumConditions: 1,
      },
      wrapText: true,
      autoHeight: true,
      hide: true,
      ...columnSize(16),
      valueGetter: ({ data }) => data?.omsStatus,
    },
    {
      headerName: 'Requested Service',
      field: SalesOrderAgTableConfig.fields.REQUESTED_SHIPPING_SERVICE,
      hide: true,
      sortable: true,
      filter: 'agTextColumnFilter',
      ...columnSize(15),
      valueGetter: ({ data }) => data?.requestedShippingService || '',
    },
    {
      headerName: 'OMS',
      field: SalesOrderAgTableConfig.fields.APP_INTEGRATION_ACCOUNT_ID,
      filter: 'AgGridCustomRadioFilterComponent', // Use your custom filter here
      hide: true,
      ...columnSize(16),
      suppressColumnsToolPanel: true, // Prevents the column from being toggled in the tool panel
    },
    {
      headerName: 'Customer PO#',
      field: SalesOrderAgTableConfig.fields.CUSTOMER_PO_NUMBER,
      sortable: false,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        defaultOption: 'contains',
        maxNumConditions: 1,
      },
      hide: true,
      ...columnSize(16),
    },

    buildColDef(ColType.ACTIONS, {
      headerName: '',
      field: SalesOrderAgTableConfig.fields.SALES_ORDER_SHIPMENTS_STATUS,
      cellRenderer: SalesOrderShipmentStatusCellRendererComponent,
      cellRendererParams: buildParams.actionsCellParams,
      width: 32,
      minWidth: 32,
      maxWidth: 32,
      valueGetter: ({ data }) => {
        if (data?.salesOrderShipments?.length > 1) {
          return 'Multiple Shipments';
        } else if (data?.salesOrderShipments?.length === 1) {
          return 'Single Shipment';
        }

        return '';
      },
    }),

    buildColDef(ColType.ACTIONS, {
      headerName: '',
      field: SalesOrderAgTableConfig.fields.ACTIONS,
      cellRenderer: SalesOrderDropdownActionCellRendererComponent,
      cellRendererParams: buildParams.actionsCellParams,
      width: 32,
      minWidth: 32,
      maxWidth: 32,
    }),
  ];

  return SALES_ORDER_AG_GRID_COL_DEFS;
}

const _DETAIL_COL_DEF_COMMON :Partial<ColDef> = {
  filter: false,
  floatingFilter: false,
  resizable: true,
  sortable: true,
  suppressMenu: true,
} as const;

export const SALES_ORDER_AG_GRID_DETAIL_COL_DEFS :ColDef[] = [
  buildColDef(
    ColType.DEFAULT,
    {
      headerName: 'Ordered SKU',
      field: SalesOrderDetailField.INTERNAL_SKU,
      ..._DETAIL_COL_DEF_COMMON,
      ...columnSize(11),
    },
    {
      routerLinkFormatter: ({ data }) =>
        data?.rfqId ? `/inventory/${data?.rfqId}/view` : '',
    },
  ),
  buildColDef(
    ColType.DEFAULT,
    {
      headerName: 'Product Name',
      field: SalesOrderDetailField.PRODUCT_NAME,
      ..._DETAIL_COL_DEF_COMMON,
      ...columnSize(15),
    },
    {
      routerLinkFormatter: ({ data }) =>
        data?.rfqId ? `/inventory/${data?.rfqId}/view` : '',
    },
  ),
  {
    headerName: 'Est. Arrival Date',
    field: SalesOrderDetailField.ESTIMATED_ARRIVAL_DATE,
    ..._DETAIL_COL_DEF_COMMON,
    ...columnSize(13),
    cellRenderer: ({ data } :ICellRendererParams) => {
      if (!data?.incomingOrders) {
        return '';
      }

      const orderedQty = formatQuantity(data.incomingOrders?.ordered_qty ?? 0);
      const fomattedDate = data.incomingOrders?.purchase_order?.arrival_due_date
        ? formatDate(
            data.incomingOrders?.purchase_order?.arrival_due_date,
            'shortDate',
            'en-US',
          )
        : '';

      return `
        <span>
          ${fomattedDate}<span class="font-italic"> x${orderedQty}</span>
        </span>
      `;
    },
  },
  {
    headerName: 'Location',
    field: SalesOrderDetailField.LOCATION,
    ..._DETAIL_COL_DEF_COMMON,
    ...columnSize(12),
    valueFormatter: ({ data } :ValueFormatterParams) => {
      if (!data?.warehouse) {
        return '';
      }

      const warehouseName = data?.warehouse?.location_name ?? '';
      if (!data?.location) {
        return warehouseName;
      }

      const locationName = data?.location?.location_name ?? '';

      return warehouseName + ' >>> ' + locationName;
    },
  },
  {
    headerName: 'Lot',
    field: SalesOrderDetailField.LOT,
    ..._DETAIL_COL_DEF_COMMON,
    ...columnSize(10),
    valueFormatter: ({ value } :ValueFormatterParams<string>) => value ?? '',
  },
  {
    headerName: 'Ordered Qty.',
    field: SalesOrderDetailField.ORDERED_QTY,
    ..._DETAIL_COL_DEF_COMMON,
    ...columnSize(8),
    valueFormatter: ({ value } :ValueFormatterParams<number>) =>
      value ? formatQuantity(value ?? 0) : '',
  },
  {
    headerName: 'UOM',
    field: SalesOrderDetailField.ORDERED_QTY,
    ..._DETAIL_COL_DEF_COMMON,
    ...columnSize(8),
    valueFormatter: ({ data } :ValueFormatterParams<any>) => data.uom?.name ?? '',
  },
  {
    headerName: 'Available Qty.',
    field: SalesOrderDetailField.AVAILABLE_QTY,
    ..._DETAIL_COL_DEF_COMMON,
    ...columnSize(8),
    valueFormatter: ({ value } :ValueFormatterParams<number>) =>
      value ? formatQuantity(value ?? 0) : '',
  },
];
